import { makeStyles, Theme } from "@material-ui/core";
import LinkOutlinedIcon from "@material-ui/icons/LinkOutlined";
import { Formik } from "formik";
import React, { ReactNode, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { FormikTextField } from "../../../components/form/Formik";
import FormError from "../../../components/form/Formik/FormError";
import { useRecaptchaFormikForm } from "../../../components/form/Formik/hooks/useRecaptchaFormikForm";
import { RecaptchaSubmitButton } from "../../../components/form/Formik/RecaptchaSubmitButton";
import { getSignUpUrl } from "../../../utils/hubRoutes";
import { Layout } from "../../components/Layout";
import { PageHelmet } from "../../components/PageHelmet";
import { useLoginEmailPage } from "./useLoginEmailPage";

const useStyles = makeStyles(
  (theme: Theme) => ({
    form: {
      display: "flex",
      flexDirection: "column",
    },
    button: {
      alignSelf: "center",
      marginTop: 10,
      [theme.breakpoints.up("xs")]: {
        marginTop: 6,
      },
    },
    notice: {
      marginTop: 24,
      textAlign: "center",
      fontSize: 12,
      fontWeight: 500,
      [theme.breakpoints.up("xs")]: {
        marginTop: 20,
      },
      [theme.breakpoints.down("xs")]: {
        "& span": {
          display: "block",
        },
      },
      "& a": {
        color: "inherit",
        fontWeight: 600,
        fontSize: 11,
      },
    },
    linkBlock: {
      textAlign: "center",
      marginTop: 24,
      fontWeight: 700,
    },
    signUpLink: {
      marginLeft: 6,
      fontWeight: 600,
    },
    send: {
      textAlign: "center",
      fontWeight: 500,
      color: "#2b9755",
      backgroundColor: "rgba(43, 151, 84, 0.08)",
      padding: "4px 8px",
      marginBottom: 8,
      borderRadius: 2,
    },
  }),
  { name: "LoginEmailPage" }
);

export const LoginEmailPage = () => {
  const { initialValues, validationSchema, onSubmit, locale, showSend, onFocus } = useLoginEmailPage();
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const { recaptchaButtonRef, onKeyPress } = useRecaptchaFormikForm();

  const link = useCallback((chunks: ReactNode) => <Link to={`/${locale}/login-password`}>{chunks}</Link>, [locale]);

  return (
    <Layout title={formatMessage({ id: "login.welcome" })}>
      <PageHelmet path="login-email" />
      <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
        <div className={classes.form} onKeyPress={onKeyPress}>
          <FormikTextField
            fullWidth
            name="email"
            placeholder="name@email-address.com"
            label={formatMessage({ id: "common.email" })}
            InputLabelProps={{ shrink: true }}
            onFocus={onFocus}
          />
          <FormError />
          {showSend && (
            <div className={classes.send}>
              <FormattedMessage id="login.email.send" />
            </div>
          )}
          <RecaptchaSubmitButton className={classes.button} ref={recaptchaButtonRef} icon={LinkOutlinedIcon}>
            <FormattedMessage id="common.signInEmail" />
          </RecaptchaSubmitButton>
          <div className={classes.notice}>
            <FormattedMessage id="login.emailNotice1" />
            <span> </span>
            <FormattedMessage id="login.emailNotice2" values={{ link }} />
          </div>
          <div className={classes.linkBlock}>
            <FormattedMessage id="login.noAccount" />
            <a href={getSignUpUrl(locale)} className={classes.signUpLink}>
              <FormattedMessage id="login.create" />
            </a>
          </div>
        </div>
      </Formik>
    </Layout>
  );
};
